import { useMemo, useState, memo, Fragment } from 'react';
import { useApp } from '../../../context/AppContext';
import {
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Area, ComposedChart, Bar, Line, LabelList,
} from 'recharts';
import { LABEL_STYLE } from '../shared/constants';
import { ChartWrapper } from '../shared/ChartWrapper';
import { NoChartData } from '../shared/NoChartData';

export const AowTrajectoryChart = memo(function AowTrajectoryChart({ indicators, hlos, selectedIdx: controlledIdx, onSelectIdx }) {
  const [logScale, setLogScale] = useState(false);
  const [localIdx, setLocalIdx] = useState(null);
  const selectedIdx = controlledIdx !== undefined ? controlledIdx : localIdx;
  const setSelectedIdx = onSelectIdx || setLocalIdx;

  // Build grouped dropdown options from hlos
  const groupedOptions = useMemo(() => {
    if (!hlos || hlos.length === 0) return [];
    const sortByTitle = (a, b) => (a.shortTitle || '').localeCompare(b.shortTitle || '', undefined, { numeric: true });
    const hloItems = hlos.filter(h => h.resultType && !h.resultType.includes('Intermediate')).sort(sortByTitle);
    const iocItems = hlos.filter(h => h.resultType && h.resultType.includes('Intermediate')).sort(sortByTitle);
    const untyped = hlos.filter(h => !h.resultType).sort(sortByTitle);

    const groups = [];
    if (hloItems.length + untyped.length > 0) {
      groups.push({ label: 'High Level Outputs', items: [...hloItems, ...untyped] });
    }
    if (iocItems.length > 0) {
      groups.push({ label: 'Intermediate Outcomes', items: iocItems });
    }
    return groups;
  }, [hlos]);

  // Map each indicator object to its index in the flat array
  const indicatorIndexMap = useMemo(() => {
    const map = new Map();
    indicators?.forEach((ind, i) => map.set(ind, i));
    return map;
  }, [indicators]);

  const selectedInd = selectedIdx != null ? indicators[selectedIdx] : null;

  const chartData = useMemo(() => {
    if (!selectedInd) return null;
    const years = [2025, 2026, 2027, 2028, 2029, 2030];
    const data = years.map(y => ({
      year: y.toString(),
      target: parseFloat(selectedInd[`target${y}`]) || 0,
      achieved: y === 2025 ? (selectedInd.achieved || 0) : null,
    }));
    if (data.every(d => d.target === 0 && (d.achieved || 0) === 0)) return null;
    return data;
  }, [selectedInd]);

  if (!indicators || indicators.length === 0) return (
    <ChartWrapper title="AoW Indicator Trajectory">
      <NoChartData message="No indicator trajectory data for this Area of Work" detail="This chart shows 2025-2030 target trajectories and actual achievement for ToC indicators. No indicators with target data were found for this Area of Work." />
    </ChartWrapper>
  );

  const exportData = chartData?.map(d => ({
    Year: d.year,
    'Planned Target': d.target,
    ...(d.achieved !== null ? { '2025 Achieved': d.achieved } : {}),
  })) || [];

  // Clamp zeros for log scale (log(0) is undefined)
  const displayData = useMemo(() => {
    if (!logScale || !chartData) return chartData;
    return chartData.map(d => ({
      ...d,
      target: Math.max(d.target, 1),
      achieved: d.achieved != null ? Math.max(d.achieved, 1) : null,
    }));
  }, [chartData, logScale]);

  // Power-of-10 ticks for log scale (1, 10, 100, ...)
  const logTicks = useMemo(() => {
    if (!logScale || !chartData) return undefined;
    const allVals = chartData.flatMap(d => [d.target, d.achieved].filter(v => v != null && v > 0));
    const maxVal = Math.max(...allVals, 1);
    const ticks = [];
    for (let p = 0; 10 ** p <= maxVal * 2; p++) ticks.push(10 ** p);
    return ticks;
  }, [chartData, logScale]);

  const truncate = (text, max) => text && text.length > max ? text.slice(0, max) + '…' : text;

  return (
    <ChartWrapper
      title="Target Trajectory (2025-2030)"
      info="Select an indicator to view its yearly target trajectory from the Theory of Change alongside actual 2025 achievement."
      description="Per-indicator target trajectory for 2025–2030."
      chartData={exportData}
      exportFilename="indicator-target-trajectory"
    >
      {/* Indicator dropdown */}
      <select
        value={selectedIdx ?? ''}
        onChange={(e) => setSelectedIdx(e.target.value === '' ? null : Number(e.target.value))}
        className="w-full mb-3 px-3 py-2 text-sm border border-[var(--border-primary)] rounded-lg bg-[var(--surface-primary)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-cgiar-green/30"
      >
        <option value="">Select an indicator…</option>
        {groupedOptions.map(group => (
          <Fragment key={group.label}>
            <option disabled>── {group.label} ──</option>
            {group.items.map(hlo => (
              <optgroup key={hlo.shortTitle} label={`  ${hlo.shortTitle}`}>
                {hlo.indicators?.filter(ind => !ind.isUnaligned).map(ind => {
                  const idx = indicatorIndexMap.get(ind);
                  if (idx === undefined) return null;
                  const label = truncate(ind.displayName || ind.indicator, 70) || hlo.shortTitle;
                  return (
                    <option key={idx} value={idx}>
                      {label} ({ind.unit || '—'})
                    </option>
                  );
                })}
              </optgroup>
            ))}
          </Fragment>
        ))}
      </select>

      {/* Indicator metadata */}
      {selectedInd && (
        <div className="mb-2 text-xs text-[var(--text-tertiary)]">
          <p className="mb-1 text-[var(--text-secondary)]">{selectedInd.indicator || selectedInd.displayName}</p>
          <div className="flex items-center gap-3">
            {selectedInd.unit && <span>Unit: {selectedInd.unit}</span>}
            {selectedInd.achievementPct != null && (
              <span>2025 progress: {Math.round(selectedInd.achievementPct)}%</span>
            )}
          </div>
        </div>
      )}

      {!chartData ? (
        <div className="flex items-center justify-center h-[200px] border-2 border-dashed border-gray-200 rounded-lg">
          <p className="text-sm text-[var(--text-tertiary)]">
            {selectedInd ? 'No target or achievement data for this indicator' : 'Select an indicator above to view its target trajectory'}
          </p>
        </div>
      ) : (
        <>
          <ResponsiveContainer width="100%" height={200}>
            <ComposedChart data={displayData} margin={{ top: 20, right: 15, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="targetAreaGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#2e9e6a" stopOpacity={0.12} />
                  <stop offset="100%" stopColor="#2e9e6a" stopOpacity={0.02} />
                </linearGradient>
                <linearGradient id="achievedBarGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#4da8d8" stopOpacity={0.9} />
                  <stop offset="100%" stopColor="#4da8d8" stopOpacity={0.6} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="year" tick={{ fontSize: 12, fill: '#6b7280' }} />
              <YAxis
                scale={logScale ? 'log' : 'linear'}
                domain={logScale ? [1, 'auto'] : [0, 'auto']}
                ticks={logTicks}
                tick={{ fontSize: 12, fill: '#6b7280' }}
                width={50}
                allowDecimals={false}
              />
              <Tooltip
                content={({ active, payload, label }) => {
                  if (!active || !payload?.length) return null;
                  const target = payload.find(p => p.dataKey === 'target');
                  const achieved = payload.find(p => p.dataKey === 'achieved' && p.value != null);
                  return (
                    <div style={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', padding: '8px 12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                      <p style={{ margin: 0, fontWeight: 600 }}>{label}</p>
                      {target && <p style={{ margin: '4px 0 0', color: '#2e9e6a' }}>Planned target: {target.value}</p>}
                      {achieved && <p style={{ margin: '4px 0 0', color: '#4da8d8' }}>2025 Achieved: {achieved.value}</p>}
                    </div>
                  );
                }}
              />
              <Area type="monotone" dataKey="target" fill="url(#targetAreaGrad)" stroke="none" tooltipType="none" />
              <Line
                type="monotone"
                dataKey="target"
                stroke="#2e9e6a"
                strokeWidth={2}
                strokeDasharray="6 3"
                dot={{ r: 4, fill: '#2e9e6a', stroke: 'none' }}
                activeDot={{ r: 6, fill: '#2e9e6a', stroke: 'none' }}
                name="target"
              />
              <Bar
                dataKey="achieved"
                fill="url(#achievedBarGrad)"
                stroke="#4da8d8"
                strokeWidth={1}
                barSize={32}
                radius={[4, 4, 0, 0]}
                name="achieved"
              >
                <LabelList dataKey="achieved" position="top" style={LABEL_STYLE} formatter={(v) => v != null ? v : ''} />
              </Bar>
            </ComposedChart>
          </ResponsiveContainer>
          <div className="flex items-center justify-center gap-5 mt-2 text-xs text-gray-500">
            <span className="flex items-center gap-1.5">
              <svg width="20" height="2"><line x1="0" y1="1" x2="20" y2="1" stroke="#2e9e6a" strokeWidth="2" strokeDasharray="4 2" /></svg>
              Planned targets
            </span>
            <span className="flex items-center gap-1.5">
              <svg width="14" height="12"><rect x="1" y="1" width="12" height="10" rx="2" fill="#4da8d8" opacity="0.8" /></svg>
              2025 achieved
            </span>
            <span className="ml-2 inline-flex rounded-full border border-gray-300 overflow-hidden">
              <button
                onClick={() => setLogScale(false)}
                className={`px-2 py-0.5 transition-colors ${!logScale ? 'bg-gray-600 text-white' : 'text-gray-400 hover:text-gray-600'}`}
              >
                Linear
              </button>
              <button
                onClick={() => setLogScale(true)}
                className={`px-2 py-0.5 transition-colors ${logScale ? 'bg-gray-600 text-white' : 'text-gray-400 hover:text-gray-600'}`}
              >
                Log
              </button>
            </span>
          </div>
        </>
      )}
    </ChartWrapper>
  );
});

/** Preview wrapper that shows traffic light header + trajectory chart for first AoW. */
export function AowTrajectoryPreview() {
  const { trafficLights } = useApp();
  const aow = trafficLights?.computed?.find(a => a.indicators?.length);
  if (!aow) return null;
  const ratingLabel = { green: 'On track', yellow: 'Moderate', red: 'Behind', unknown: 'No data' }[aow.rating] || 'No data';
  const ratingColor = { green: '#16a34a', yellow: '#ca8a04', red: '#dc2626', unknown: '#9ca3af' }[aow.rating] || '#9ca3af';
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderBottom: '1px solid #e5e7eb' }}>
        <span style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: ratingColor, flexShrink: 0 }} />
        <span style={{ fontWeight: 600, fontSize: 13 }}>{aow.name}</span>
        <span style={{ fontSize: 11, color: ratingColor, fontWeight: 600 }}>{ratingLabel}</span>
        {aow.achievementPct != null && (
          <span style={{ marginLeft: 'auto', fontSize: 11, color: '#6b7280' }}>{Math.round(aow.achievementPct)}%</span>
        )}
      </div>
      <AowTrajectoryChart indicators={aow.indicators} hlos={aow.hlos} selectedIdx={0} />
    </div>
  );
}
